import React, { useCallback } from 'react';
import { ChapterEndItem } from '../../types';
import { DEFAULT_CHAPTER_END_CONTINUE_TEXT } from '../../systems/chapter-end-content';
import inline from './inline-editor.module.css';
import studio from './scenario-studio.module.css';
import type { InlineScenarioChangeOptions } from './use-inline-block-history';

function EditableText({
  value,
  className,
  onChange,
  placeholder,
}: {
  value: string;
  className?: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLSpanElement>) => {
      onChange(e.currentTarget.textContent?.trim() ?? '');
    },
    [onChange]
  );

  return (
    <span
      className={className}
      contentEditable
      suppressContentEditableWarning
      onBlur={handleBlur}
    >
      {value || placeholder || ''}
    </span>
  );
}

interface InlineChapterEndEditorProps {
  item: ChapterEndItem;
  editMode: boolean;
  sceneIds: string[];
  onChange: (item: ChapterEndItem, options?: InlineScenarioChangeOptions) => void;
}

export const InlineChapterEndEditor: React.FC<InlineChapterEndEditorProps> = ({
  item,
  editMode,
  onChange,
}) => {
  const patch = (partial: Partial<ChapterEndItem>, recordHistory = false) =>
    onChange(
      { ...item, ...partial },
      recordHistory ? { recordBlockHistory: true } : undefined
    );

  const continueText = item.continueText?.trim() || DEFAULT_CHAPTER_END_CONTINUE_TEXT;

  if (!editMode) {
    return (
      <>
        {item.eyebrow && <span className={inline.chapterEndEyebrow}>{item.eyebrow}</span>}
        <span className={inline.slText}>{item.heading || 'Fin de chapitre'}</span>
        {(item.missionLabel || item.missionIndex) && (
          <span className={inline.chapterEndMeta}>
            {item.missionLabel ?? `Mission ${item.missionIndex}`}
            {item.missionTotal > 1 ? ` / ${item.missionTotal}` : ''}
          </span>
        )}
        {item.keyIdea && <span className={inline.chapterEndKeyIdea}>{item.keyIdea}</span>}
        {item.reflexes.length > 0 && (
          <ul className={inline.chapterEndList}>
            {item.reflexes.filter(Boolean).map((reflex, i) => (
              <li key={i}>{reflex}</li>
            ))}
          </ul>
        )}
        <div className={inline.choiceRow}>
          <span className={inline.choiceBtn}>
            <span className={inline.choiceBtnLabel}>≡ Menu</span>
          </span>
          <span className={inline.choiceBtn}>
            <span className={inline.choiceBtnLabel}>{continueText}</span>
          </span>
        </div>
      </>
    );
  }

  return (
    <div className={inline.chapterEndEditor}>
      <div className={inline.slMetaRow}>
        <div className={inline.metaField}>
          <span className={inline.metaFieldLabel}>Surtitre</span>
          <input
            className={studio.input}
            value={item.eyebrow}
            onChange={(e) => patch({ eyebrow: e.target.value })}
            placeholder="Ex. Chapitre 1"
          />
        </div>
        <div className={`${inline.metaField} ${inline.metaFieldGrow}`}>
          <span className={inline.metaFieldLabel}>Titre</span>
          <input
            className={studio.input}
            value={item.heading}
            onChange={(e) => patch({ heading: e.target.value })}
            placeholder="Titre de fin de chapitre"
          />
        </div>
      </div>

      <div className={inline.slMetaRow}>
        <div className={inline.metaField}>
          <span className={inline.metaFieldLabel}>Libellé mission</span>
          <input
            className={studio.input}
            value={item.missionLabel ?? ''}
            onChange={(e) => patch({ missionLabel: e.target.value || undefined })}
            placeholder="Mission 1"
          />
        </div>
        <div className={inline.metaField}>
          <span className={inline.metaFieldLabel}>Index</span>
          <input
            type="number"
            min={1}
            className={studio.input}
            value={item.missionIndex}
            onChange={(e) => patch({ missionIndex: parseInt(e.target.value, 10) || 1 })}
          />
        </div>
        <div className={inline.metaField}>
          <span className={inline.metaFieldLabel}>Total</span>
          <input
            type="number"
            min={1}
            className={studio.input}
            value={item.missionTotal}
            onChange={(e) => patch({ missionTotal: parseInt(e.target.value, 10) || 1 })}
          />
        </div>
        <div className={inline.metaField}>
          <span className={inline.metaFieldLabel}>Boucliers max</span>
          <input
            type="number"
            min={0}
            className={studio.input}
            value={item.chapterShieldsMax ?? 0}
            onChange={(e) =>
              patch({ chapterShieldsMax: parseInt(e.target.value, 10) || undefined })
            }
          />
        </div>
      </div>

      <div className={inline.metaField}>
        <span className={inline.metaFieldLabel}>Légende boucliers</span>
        <input
          className={studio.input}
          value={item.chapterShieldsCaption ?? ''}
          onChange={(e) => patch({ chapterShieldsCaption: e.target.value || undefined })}
          placeholder="Boucliers de ce chapitre"
        />
      </div>

      <div className={inline.metaField}>
        <span className={inline.metaFieldLabel}>Idée clé</span>
        <textarea
          className={studio.textarea}
          rows={2}
          value={item.keyIdea}
          onChange={(e) => patch({ keyIdea: e.target.value })}
          placeholder="Leçon ou synthèse du chapitre…"
        />
      </div>

      <div className={inline.chapterEndSection}>
        <span className={inline.chapterEndSectionLabel}>Réflexes</span>
        {item.reflexes.map((reflex, idx) => (
          <div key={idx} className={inline.chapterEndCard}>
            <textarea
              className={studio.textarea}
              rows={2}
              value={reflex}
              onChange={(e) => {
                const reflexes = [...item.reflexes];
                reflexes[idx] = e.target.value;
                patch({ reflexes });
              }}
            />
            <button
              type="button"
              className={inline.choiceOptionRemove}
              onClick={() => patch({ reflexes: item.reflexes.filter((_, i) => i !== idx) }, true)}
              title="Supprimer ce réflexe"
            >
              ×
            </button>
          </div>
        ))}
        <button
          type="button"
          className={`${studio.btnSecondary} ${inline.chapterEndAddBtn}`}
          onClick={() => patch({ reflexes: [...item.reflexes, ''] }, true)}
        >
          + Réflexe
        </button>
      </div>

      <div className={inline.chapterEndSection}>
        <span className={inline.chapterEndSectionLabel}>Boutons de navigation</span>
        <p className={inline.chapterEndHint}>
          Le bouton ≡ Menu est toujours affiché en jeu. La suite passe automatiquement à la scène suivante.
        </p>
        <div className={inline.metaField}>
          <span className={inline.metaFieldLabel}>Libellé du bouton continuer</span>
          <EditableText
            value={item.continueText ?? ''}
            className={inline.choiceOptionText}
            onChange={(text) => patch({ continueText: text || undefined })}
            placeholder={DEFAULT_CHAPTER_END_CONTINUE_TEXT}
          />
        </div>
      </div>
    </div>
  );
};
